import React from 'react';
import {Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import {NAVY, ORANGE, PageHeader, BandChip, riskBandColor, RISK_BAND_COLORS, heatFill} from '../components/widgets.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, whole, riskList} from '../networkData';
import {isSuppressedCell} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

/* Slides 20–21 — "Cyber Risk by Region", renamed "Cyber Risk by School" per
   the deck's "Replace region → school" note (SND-20-01, SND-21-01 — one card;
   slide 21 is the same table scrolled).

     SND-20-01  risk × school heat-map   SND.S_6.C_3   TableRubricCard

   The card's first column is the school; each other column is one risk, with
   a prevalence value and band per school. Drawn as the deck draws it — risks
   down the side, schools across — in the fixed risk order of the "Select a
   risk" list (SND.S_7.C_2). Cell colour is the datahouse's band. The table is
   transposed, so its sortable columns are the risk name and one per school:
   clicking a school's header ranks the risks by that school (QA 9 Oct 2026). */

const Legend = () => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2.5}}>
        {Object.keys(RISK_BAND_COLORS).map((band) => (
            <BandChip key={band} label={band} color={riskBandColor(band)}/>
        ))}
    </Box>
);

const schoolHeadCell = {backgroundColor: NAVY, color: '#fff', fontWeight: 700, textAlign: 'center', borderBottom: 'none'};

// The table sits in its own component so the sort/filter hook runs below the page's loading branches.
const RiskBySchoolTable = ({rows, schools}) => {
    const columns = [
        {key: 'risk', label: 'Risk', type: 'text', accessor: (r) => r.label},
        ...schools.map((school, i) => ({
            key: `school_${i}`, label: school, type: 'percent',
            // A school under the n floor has no value to rank by.
            accessor: (r) => (isSuppressedCell(r.col.cell_data?.[i]) ? null : whole(r.col.values?.[i])),
        })),
    ];
    const t = useTableControls(rows, columns);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.04)'}}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <SortFilterHeaderCell {...t.headerProps('risk')} sx={{...schoolHeadCell, textAlign: 'left'}}/>
                            {schools.map((school, i) => (
                                <SortFilterHeaderCell key={`school_${i}`} {...t.headerProps(`school_${i}`)} sx={schoolHeadCell}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={1 + schools.length} sx={{textAlign: 'center', color: '#6A7282', fontSize: '13px', py: 3}}>No risk matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map(({label, col}) => (
                            <TableRow key={label}>
                                <TableCell sx={{backgroundColor: NAVY, color: '#fff', fontWeight: 700, fontSize: '13px', py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.1)'}}>
                                    {label}
                                </TableCell>
                                {schools.map((school, i) => {
                                    const cd = col.cell_data?.[i];
                                    const sup = isSuppressedCell(cd);           // a school under the n floor: placeholder, no colour
                                    const pct = sup ? null : whole(col.values?.[i]);
                                    const band = sup ? null : cd?.band;
                                    return (
                                        <TableCell key={`school_${i}`} sx={{
                                            // The National "by Region" heat-map's cell: band colour at 0.8, navy figure.
                                            textAlign: 'center', fontWeight: 700, fontSize: '14px', py: 1.5, borderBottom: '1px solid #fff',
                                            color: NAVY,
                                            backgroundColor: band ? heatFill(riskBandColor(band)) : undefined,
                                        }}>
                                            {sup ? <SuppressedValue variant="cell"/> : (pct === null ? '—' : `${pct}%`)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const CYBER_RISK_BY_SCHOOL_SECTION_CODE = 'S_6';
const CYBER_RISK_BY_AGE_SECTION_CODE = 'S_7';

const CARD_SECTIONS = [
    CYBER_RISK_BY_SCHOOL_SECTION_CODE,
    CYBER_RISK_BY_AGE_SECTION_CODE,
];

const CyberRiskBySchoolTab = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });

    if (loading) {
        return <CircularLoader />;
    }

    if (!dashboardData) {
        return <NoDataAvailable />;
    }
    const cols = findCard(dashboardData, CYBER_RISK_BY_SCHOOL_SECTION_CODE, 'C_3')?.table_objects || [];
    const schoolCol = cols.find((c) => c.column_type === 'text') || cols[0];
    const SCHOOLS = schoolCol?.values || [];

    const order = riskList(findCard(dashboardData, CYBER_RISK_BY_AGE_SECTION_CODE, 'C_2'));
    const posOf = (name) => {
        const i = order.findIndex((r) => r.name === name);
        return i < 0 ? order.length : i;
    };
    // One row per risk, in the "Select a risk" order. A risk the card sends
    // twice under the same name ("Any Cyber Risk", codes expe_CPCI_CyberRisks
    // and expe_CyberRisks) is listed once.
    const seen = new Set();
    const RISK_ROWS = cols
        .filter((c) => c !== schoolCol)
        .filter((c) => {
            const name = String(c.column_name || '').trim();
            if (seen.has(name)) return false;
            seen.add(name);
            return true;
        })
        .map((c, idx) => ({idx, label: c.column_name, col: c}))
        .sort((a, b) => (posOf(a.label) - posOf(b.label)) || (a.idx - b.idx));

    return (
    <Box>
        <PageHeader
            title="Cyber Risk by School"
            subtitle="Which schools report higher cyber risk exposure and need targeted support?"
        />

        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 2}}>
            Network Cyber-Risk Profile by School
        </Typography>
        <Legend/>

        <RiskBySchoolTable rows={RISK_ROWS} schools={SCHOOLS}/>

        {/* Decision guidance (LA-SND-03Nov-02): the National "by Region" box, worded for schools. */}
        <Box sx={{mt: 3, border: '1px solid #C7D9E8', borderLeft: `4px solid ${NAVY}`, borderRadius: '8px', backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)', p: {xs: 2, md: 2.5}}}>
            <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, mb: 0.5}}>
                Decision guidance:
            </Typography>
            <Typography sx={{fontSize: '13px', fontWeight: 700, color: ORANGE, mb: 1.2}}>
                Use with care
            </Typography>
            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6, mb: 1}}>
                Differences should prompt support, not assumptions about any school.
            </Typography>
            <Typography sx={{fontSize: '13px', fontWeight: 600, color: NAVY, lineHeight: 1.6}}>
                Prioritize schools where high risk overlaps with weak competency level and support context.
            </Typography>
        </Box>
    </Box>
    );
};

export default CyberRiskBySchoolTab;
